// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

/**
 * Represents a string value that is persisted in either session or local storage.
 */
export class PersistedString {
    public constructor(
        private readonly key: string,
        private readonly type: 'session' | 'local' = 'session'
    ) {}

    private get storage(): Storage {
        return this.type === 'session' ? sessionStorage : localStorage;
    }

    public get(): string | undefined {
        const value = this.storage.getItem(this.key);
        return value !== null ? value : undefined;
    }

    public set(value: string | undefined) {
        if (value === undefined) {
            this.storage.removeItem(this.key);
        } else {
            this.storage.setItem(this.key, value);
        }
    }

    public clear() {
        this.storage.removeItem(this.key);
    }
}

/**
 * Represents a value of type T that is persisted in either session or local storage.
 */
export class PersistedValue<T> {
    public constructor(
        private readonly key: string,
        private readonly type: 'session' | 'local' = 'session'
    ) {}

    private get storage(): Storage {
        return this.type === 'session' ? sessionStorage : localStorage;
    }

    public get(): T | undefined {
        const value = this.storage.getItem(this.key);
        return value !== null ? JSON.parse(value) : undefined;
    }

    public set(value: T | undefined) {
        if (value === undefined) {
            this.storage.removeItem(this.key);
        } else {
            this.storage.setItem(this.key, JSON.stringify(value));
        }
    }

    public clear() {
        this.storage.removeItem(this.key);
    }
}
